<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header align-items-start">
            <div class="card-group-title">
                <h4 class="card-title">
                    {{ $t('org.dashboard.Cost by product') }}
                </h4>
                <h6 class="card-subtitle">
                    {{ $t('org.dashboard.Percentage of amount spent by product') }}
                </h6>
            </div>
            <b-card-actions-container
                v-if="!editMode && pieChartData.length && !viewTypeIsMobile"
                class="card-actions-container"
                :open="isVisible"
                @card:collapse="triggerCollapse"
            />
        </div>
        <DoughnutChart
            :data="chartData"
            :options="chartOptions"
            :styles="fetchedChartResponsiveStyle"
            class="dashboard-chart px-3"
            :plugins="[innerLabel]"
        >
        </DoughnutChart>
        <div
            ref="REF_COLLAPSE"
            :style="collapseResponsiveStyle"
        >
            <CardProductsTable
                v-show="!editMode && isVisible"
                @modal:showDetail="modalShowDetail"
                :lastMonthProducts="productsCostData.lastMonthProducts"
                :thisMonthProducts="productsCostData.thisMonthProducts"
            />
        </div>
    </b-overlay>
</template>

<script>
import BCardActionsContainer from '@/components/BCardActionsContainer.vue'
import CreateVueChart from '@/components/CreateVueChart.js'
import { ref, toRefs, computed, getCurrentInstance, watch, onUnmounted, nextTick, inject } from '@vue/composition-api'
import useDashboard from '@/composables/useDashboard'
import { Chart } from 'chart.js'
import ChartJSPluginDatalabels from 'chartjs-plugin-datalabels'
import useDashboardRender from '@/composables/useDashboardRender'
import CardProductsTable from './CardProductsTable'
export default {
    props: ['editMode', 'isOpen'],
    components: {
        DoughnutChart: CreateVueChart('Doughnut'),
        CardProductsTable,
        BCardActionsContainer
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const theme = inject('theme')
        const { isOpen } = toRefs(props)
        const {
            getSummaryProducts
        } = useDashboard()
        const {
            viewType
        } = useDashboardRender()
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const chartResponsiveStyle = ref({
            position: 'absolute',
            top: '62px',
            bottom: 0,
            left: '0',
            right: '0',
            'margin': '1.5rem 1.7rem'
        })
        const collapseResponsiveStyle = {
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%'
        }
        const isVisible = ref(false)
        const REF_COLLAPSE = ref(null)
        const triggerCollapse = (status) => {
            context.emit('action:showTable', { component: 'card-products', status })
        }
        watch(isOpen, (value, oldValue) => {
            isVisible.value = value
            nextTick(() => {
                const tableHeight = value ? vm.$refs['REF_COLLAPSE'].offsetHeight : 0
                chartResponsiveStyle.value.bottom = value ? `${tableHeight}px` : 0
            })
        })
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, productsCostData] = getSummaryProducts(currentParams.value)
        const thisMonthCost = computed(() => productsCostData.value.thisMonthProducts)
        const pieChartData = computed(() => {
            let fullData = []
            if (!thisMonthCost.value) return []
            if (thisMonthCost.value.length > 5) {
                const etcDataForPieChart = thisMonthCost.value.slice(4, thisMonthCost.value.length).reduce((accumulator, currentValue) => {
                    accumulator.cost += currentValue.cost
                    return accumulator
                }, { productCode: 'ETC', cost: 0 })
                fullData = thisMonthCost.value.slice(0, 4).concat(etcDataForPieChart)
            } else {
                fullData = thisMonthCost.value.slice()
            }
            return fullData.filter(item => item.cost > 0)
        })
        const setTooltipText = (tooltipItem, data) => {
            const label = data.datasets[0].labels[tooltipItem.index] || ''
            const value = data.datasets[0].data[tooltipItem.index]
            const output = ` ${label} : ${vm.$_getDollar(value)}`
            return output
        }
        const setLabelText = () => pieChartData.value.map(item => item.productCode)
        const totalProductsCost = computed(() => pieChartData.value.map(item => item.cost).reduce((prev, cur) => prev + cur, 0))
        const chartData = computed(() => {
            return {
                labels: pieChartData.value.length ? vm.setLabelText() : ['-', '-', '-', '-', '-'],
                datasets: [
                    {
                        labels: pieChartData.value.length ? pieChartData.value.map(item => item.productCode) : [],
                        data: pieChartData.value.length ? pieChartData.value.map(item => item.cost) : [0, 0, 0, 100, 0],
                        backgroundColor: [
                            '#ffe700',
                            '#FFA1A1',
                            '#00CFE8',
                            '#826bf8',
                            '#00d4bd'
                        ],
                        borderWidth: [0.001, 0.001, 0.001, 0.001, 0.001],
                        pointStyle: 'rectRounded'
                    }
                ]
            }
        })
        const saveHoveredIdx = (idx) => { hoveredIdx.value = idx }
        const hoveredIdx = ref(0)
        const innerLabel = {
            id: 'innerLabel',
            afterDatasetDraw (chart, args, pluginOptions) {
                const { ctx } = chart
                const meta = args.meta
                if (!meta.data[0]) return
                const xCoor = meta.data[0]._view.x
                const yCoor = meta.data[0]._view.y
                ctx.save()
                ctx.textAlign = 'center'
                ctx.font = '15px "public Sans"'
                ctx.fillStyle = theme.value === 'light' ? '#535353' : '#fff'
                ctx.fillText(pieChartData.value.length ? chart.data.datasets[0].labels[hoveredIdx.value] : vm.$t('org.dashboard.no data'), xCoor, yCoor - 10)
                ctx.font = '18px "public Sans"'
                ctx.fillText(`${pieChartData.value.length ? (chart.data.datasets[0].data[hoveredIdx.value] / totalProductsCost.value * 100).toFixed() : '- '}%`, xCoor, yCoor + 20)
                ctx.restore()
            }
        }
        const modalShowDetail = ({ productCode }) => {
            context.emit('modal:showDetail', { productCode })
        }
        const chartOptions = computed(() => {
            return {
                plugins: {
                    datalabels: {
                        color: '#FFFFFF',
                        formatter: value => {
                            if (!pieChartData.value.length) {
                                return ''
                            } else {
                                return Number((value / totalProductsCost.value * 100).toFixed()) !== 0 ? `${(value / totalProductsCost.value * 100).toFixed()}%` : ''
                            }
                        }
                    }
                },
                onClick: (e, data) => {
                    if (pieChartData.value.length && data[0]) {
                        const productCode = pieChartData.value[data[0]._index].productCode
                        if (productCode.toLowerCase() !== 'etc') {
                            modalShowDetail({ productCode })
                        }
                    }
                },
                onHover: function (e, el) {
                    if (pieChartData.value.length) {
                        if (el.length && el[0]._index !== hoveredIdx.value) saveHoveredIdx(el[0]._index)
                        e.target.style.cursor = el[0] && el[0]['_model'].label.toLowerCase() !== 'etc' ? 'pointer' : 'default'
                    }
                },
                responsive: true,
                maintainAspectRatio: false,
                responsiveAnimationDuration: 500,
                cutoutPercentage: 65,
                legend: {
                    fontColor: '#FFFFFF',
                    position: 'bottom',
                    align: 'center',
                    labels: {
                        usePointStyle: true,
                        padding: 23,
                        boxWidth: 7,
                        fontSize: 11,
                        fontColor: '#B4B4B4'
                    }
                },
                tooltips: {
                    enabled: pieChartData.value.length,
                    callbacks: {
                        label: vm.setTooltipText
                    },
                    shadowOffsetX: 1,
                    shadowOffsetY: 1,
                    shadowBlur: 80
                }
            }
        })
        watch(currentParams, (value, oldValue) => {
            getSummaryProducts(value)
        }, {
            deep: true
        })
        Chart.plugins.register(ChartJSPluginDatalabels)
        onUnmounted(() => {
            Chart.plugins.unregister(ChartJSPluginDatalabels)
        })
        const fetchedChartResponsiveStyle = computed(() => chartResponsiveStyle.value)
        return {
            chartResponsiveStyle,
            chartData,
            chartOptions,
            setTooltipText,
            setLabelText,
            productsCostData,
            status,
            pieChartData,
            innerLabel,
            isVisible,
            triggerCollapse,
            modalShowDetail,
            REF_COLLAPSE,
            collapseResponsiveStyle,
            fetchedChartResponsiveStyle,
            viewTypeIsMobile
        }
    }
}
</script>

<style>
.position-top-50 {
    top: 100px;
}
</style>
